import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Check, X, Eye, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import Header from '../../components/Header';

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await api.get('/admin/applications');
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (companyId) => {
    setProcessing(true);
    try {
      await api.post(`/admin/companies/${companyId}/approve`, { notes });
      toast.success('Company approved successfully');
      setNotes('');
      setSelectedApp(null);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to approve company');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (companyId) => {
    if (!notes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/admin/companies/${companyId}/reject`, { notes });
      toast.success('Company rejected');
      setNotes('');
      setSelectedApp(null);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to reject company');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading applications...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-outfit font-bold text-ocean-900">Applications Queue</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1 sm:mt-2">{applications.length} pending applications</p>
        </div>

        {applications.length === 0 ? (
          <Card>
            <CardContent className="p-8 sm:p-12 text-center text-slate-600">
              <Building2 className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4 text-slate-400" />
              <p className="text-sm sm:text-base">No pending applications</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {applications.map((app) => (
              <Card key={app.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-base sm:text-xl truncate">{app.legal_name}</CardTitle>
                      <p className="text-xs sm:text-sm text-slate-600 mt-1">{app.country}</p>
                    </div>
                    <Badge variant="outline" className="bg-orange-50 text-orange-600 text-xs flex-shrink-0">
                      PENDING
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                  <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                    <div>
                      <span className="text-slate-600">DBA:</span> {app.dba || 'N/A'}
                    </div>
                    <div>
                      <span className="text-slate-600">Services:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {app.services.slice(0, 4).map(s => (
                          <Badge key={s} variant="secondary" className="text-[10px] sm:text-xs">{s}</Badge>
                        ))}
                        {app.services.length > 4 && (
                          <Badge variant="secondary" className="text-[10px] sm:text-xs">+{app.services.length - 4}</Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-600">Modes:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {app.modes.map(m => (
                          <Badge key={m} variant="secondary" className="text-[10px] sm:text-xs">{m}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-slate-600">Years in Business:</span> {app.years_in_business || 'N/A'}
                    </div>
                    <div>
                      <span className="text-slate-600">Applied:</span> {new Date(app.created_at).toLocaleDateString()}
                    </div>
                  </div>

                  {selectedApp === app.id ? (
                    <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                      <Textarea
                        placeholder="Add notes (optional for approval, required for rejection)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        className="text-sm"
                      />
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button
                          onClick={() => handleApprove(app.id)}
                          disabled={processing}
                          className="flex-1 bg-green-500 hover:bg-green-600 text-sm h-9 sm:h-10"
                          data-testid={`approve-${app.id}`}
                        >
                          <Check className="mr-2 h-4 w-4" />
                          {processing ? 'Processing...' : 'Approve'}
                        </Button>
                        <Button
                          onClick={() => handleReject(app.id)}
                          disabled={processing}
                          variant="destructive"
                          className="flex-1 text-sm h-9 sm:h-10"
                          data-testid={`reject-${app.id}`}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Reject
                        </Button>
                        <Button
                          onClick={() => { setSelectedApp(null); setNotes(''); }}
                          variant="outline"
                          className="text-sm h-9 sm:h-10"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 sm:mt-4 flex gap-2">
                      <Button
                        onClick={() => setSelectedApp(app.id)}
                        className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-sm h-9 sm:h-10"
                        data-testid={`review-${app.id}`}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Review
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminApplications;
